import { trackMediaListening } from '@/utils/listeningActivity'
import { ref, shallowRef, watch } from 'vue'
import { formatTimeCompact } from '@/utils/format'

export function useVideoPlayback({
  displaySubtitles,
  playMode,
  onErrorCallback,
}) {
  const mediaElement = shallowRef(null)
  const sound = shallowRef(null)
  const isPlaying = ref(false)
  const activeSoundId = ref(null)
  const currentTime = ref(0)
  const audioDuration = ref(0)
  const volume = ref(1)
  const playbackRate = ref(1)
  const progressPercentage = ref(0)
  const currentSubtitleIndex = ref(0)
  const audioError = ref(null)
  const tooltipPosition = ref(0)
  const tooltipTime = ref('00:00')
  const showTooltip = ref(false)

  let timeUpdateFrame = null
  let removeMediaListeners = null
  let stopListening = null
  let suppressedPauseEvents = 0
  let onPauseCallback = null
  let onEndedCallback = null
  let onPlayCallback = null

  function setOnPauseCallback(callback) {
    onPauseCallback = callback
  }

  function setOnPlayCallback(callback) {
    onPlayCallback = callback
  }

  function setOnEndedCallback(callback) {
    onEndedCallback = callback
  }

  function getSubtitleList() {
    return Array.isArray(displaySubtitles.value) ? displaySubtitles.value : []
  }

  function findSubtitleIndexAtTime(time, hintIndex = currentSubtitleIndex.value) {
    const subtitles = getSubtitleList()
    if (!subtitles.length) return -1

    const hinted = subtitles[hintIndex]
    if (hinted && time >= hinted.start && time <= hinted.end) return hintIndex

    const next = subtitles[hintIndex + 1]
    if (next && time >= next.start && time <= next.end) return hintIndex + 1

    const previous = subtitles[hintIndex - 1]
    if (previous && time >= previous.start && time <= previous.end) return hintIndex - 1

    let left = 0
    let right = subtitles.length - 1
    while (left <= right) {
      const middle = Math.floor((left + right) / 2)
      const subtitle = subtitles[middle]
      if (time < subtitle.start) right = middle - 1
      else if (time > subtitle.end) left = middle + 1
      else return middle
    }

    return -1
  }

  function syncTimeFromMedia() {
    const element = mediaElement.value
    if (!element) return

    currentTime.value = Number.isFinite(element.currentTime) ? element.currentTime : 0
    audioDuration.value = Number.isFinite(element.duration) ? element.duration : audioDuration.value
    progressPercentage.value = audioDuration.value > 0
      ? Math.min(100, (currentTime.value / audioDuration.value) * 100)
      : 0
    checkCurrentSubtitle()
  }

  function startTimeUpdate() {
    stopTimeUpdate()
    const update = () => {
      const element = mediaElement.value
      if (!element || element.paused || element.ended) {
        timeUpdateFrame = null
        return
      }
      syncTimeFromMedia()
      timeUpdateFrame = requestAnimationFrame(update)
    }
    update()
  }

  function stopTimeUpdate() {
    if (timeUpdateFrame !== null) {
      cancelAnimationFrame(timeUpdateFrame)
      timeUpdateFrame = null
    }
  }

  function handleVideoError(error) {
    const resolvedError = error?.target?.error || error
    audioError.value = resolvedError
    onErrorCallback?.(resolvedError)
  }

  function playCurrent() {
    const element = mediaElement.value
    if (!element) return null
    activeSoundId.value = 1
    const playRequest = element.play()
    if (playRequest?.catch) {
      playRequest.catch((error) => {
        isPlaying.value = false
        activeSoundId.value = null
        handleVideoError(error)
      })
    }
    return 1
  }

  function pauseCurrent({ suppressCallback = false } = {}) {
    const element = mediaElement.value
    if (!element || element.paused) return
    if (suppressCallback) suppressedPauseEvents += 1
    element.pause()
  }

  function stopCurrent() {
    const element = mediaElement.value
    if (!element) return
    pauseCurrent({ suppressCallback: true })
    element.currentTime = 0
    currentTime.value = 0
    progressPercentage.value = 0
    isPlaying.value = false
    activeSoundId.value = null
  }

  function seekCurrent(time) {
    const element = mediaElement.value
    const nextTime = Number(time)
    if (!element || !Number.isFinite(nextTime)) return
    const boundedTime = Math.min(Math.max(nextTime, 0), audioDuration.value || nextTime)
    element.currentTime = boundedTime
    currentTime.value = boundedTime
    progressPercentage.value = audioDuration.value > 0
      ? (boundedTime / audioDuration.value) * 100
      : 0
  }

  function isCurrentPlaying() {
    const element = mediaElement.value
    return Boolean(element && !element.paused && !element.ended)
  }

  function createCompatibilityController() {
    return {
      play: () => playCurrent(),
      pause: (options) => pauseCurrent(options),
      stop: () => stopCurrent(),
      seek: (time) => {
        if (time === undefined) return mediaElement.value?.currentTime ?? 0
        seekCurrent(time)
        return mediaElement.value?.currentTime ?? 0
      },
      playing: () => isCurrentPlaying(),
      volume: (value) => {
        if (value === undefined) return mediaElement.value?.volume ?? volume.value
        setVolume(value)
        return value
      },
      rate: (value) => {
        if (value === undefined) return mediaElement.value?.playbackRate ?? playbackRate.value
        setPlaybackRate(value)
        return value
      },
      duration: () => mediaElement.value?.duration ?? audioDuration.value,
      unload: () => stopCurrent(),
    }
  }

  function attachVideoElement(element) {
    if (!element || mediaElement.value === element) return
    detachVideoElement()

    mediaElement.value = element
    stopListening = trackMediaListening(element)
    sound.value = createCompatibilityController()
    element.volume = volume.value
    element.playbackRate = playbackRate.value

    const handleMetadata = () => {
      audioDuration.value = Number.isFinite(element.duration) ? element.duration : 0
      syncTimeFromMedia()
    }
    const handlePlay = () => {
      isPlaying.value = true
      activeSoundId.value = 1
      startTimeUpdate()
      onPlayCallback?.()
    }
    const handlePause = () => {
      const shouldSuppressCallback = suppressedPauseEvents > 0
      if (shouldSuppressCallback) suppressedPauseEvents -= 1
      isPlaying.value = false
      stopTimeUpdate()
      if (!shouldSuppressCallback) onPauseCallback?.()
    }
    const handleEnded = () => {
      isPlaying.value = false
      activeSoundId.value = null
      stopTimeUpdate()
      onEndedCallback?.()
    }
    const handleTimeUpdate = () => syncTimeFromMedia()
    const handleError = (event) => handleVideoError(event)

    element.addEventListener('loadedmetadata', handleMetadata)
    element.addEventListener('durationchange', handleMetadata)
    element.addEventListener('play', handlePlay)
    element.addEventListener('pause', handlePause)
    element.addEventListener('ended', handleEnded)
    element.addEventListener('timeupdate', handleTimeUpdate)
    element.addEventListener('seeked', handleTimeUpdate)
    element.addEventListener('error', handleError)

    removeMediaListeners = () => {
      element.removeEventListener('loadedmetadata', handleMetadata)
      element.removeEventListener('durationchange', handleMetadata)
      element.removeEventListener('play', handlePlay)
      element.removeEventListener('pause', handlePause)
      element.removeEventListener('ended', handleEnded)
      element.removeEventListener('timeupdate', handleTimeUpdate)
      element.removeEventListener('seeked', handleTimeUpdate)
      element.removeEventListener('error', handleError)
    }

    if (element.readyState >= 1) handleMetadata()
  }

  function detachVideoElement(element = mediaElement.value) {
    if (element && mediaElement.value && element !== mediaElement.value) return
    stopTimeUpdate()
    stopListening?.()
    stopListening = null
    removeMediaListeners?.()
    removeMediaListeners = null

    if (mediaElement.value) {
      mediaElement.value.pause()
    }

    mediaElement.value = null
    sound.value = null
    activeSoundId.value = null
    isPlaying.value = false
    suppressedPauseEvents = 0
  }

  function checkCurrentSubtitle() {
    const subtitles = getSubtitleList()
    if (!subtitles.length) return

    if (playMode.value === 'reading' && isPlaying.value) {
      const subtitle = subtitles[currentSubtitleIndex.value]
      if (subtitle && currentTime.value >= subtitle.end) {
        pauseCurrent({ suppressCallback: true })
        seekCurrent(Math.max(Number(subtitle.start) || 0, Number(subtitle.end) - 0.01))
      }
      return
    }

    const subtitleIndex = findSubtitleIndexAtTime(currentTime.value)
    if (subtitleIndex !== -1 && subtitleIndex !== currentSubtitleIndex.value) {
      currentSubtitleIndex.value = subtitleIndex
    }
  }

  function getCurrentSubtitle() {
    const subtitleIndex = findSubtitleIndexAtTime(currentTime.value)
    return subtitleIndex === -1 ? null : getSubtitleList()[subtitleIndex]
  }

  function handleProgressClick(event) {
    const progressBar = event.currentTarget
    if (!progressBar || !audioDuration.value) return
    const rectangle = progressBar.getBoundingClientRect()
    const percentage = Math.min(100, Math.max(0, ((event.clientX - rectangle.left) / rectangle.width) * 100))
    const time = (percentage / 100) * audioDuration.value
    seekCurrent(time)

    const subtitleIndex = findSubtitleIndexAtTime(time)
    if (subtitleIndex !== -1) currentSubtitleIndex.value = subtitleIndex
  }

  function handleProgressHover(event) {
    const progressBar = event.currentTarget
    if (!progressBar || !audioDuration.value) return
    const rectangle = progressBar.getBoundingClientRect()
    const offset = event.clientX - rectangle.left
    const percentage = (offset / rectangle.width) * 100
    if (percentage < 0 || percentage > 100) return
    tooltipPosition.value = offset
    tooltipTime.value = formatTime((percentage / 100) * audioDuration.value)
    showTooltip.value = true
  }

  function hideTimeTooltip() {
    showTooltip.value = false
  }

  function setVolume(value) {
    const nextVolume = Math.min(1, Math.max(0, Number(value)))
    volume.value = nextVolume
    if (mediaElement.value) mediaElement.value.volume = nextVolume
  }

  function setPlaybackRate(rate) {
    const nextRate = Number(rate)
    if (!Number.isFinite(nextRate) || nextRate <= 0) return
    playbackRate.value = nextRate
    if (mediaElement.value) mediaElement.value.playbackRate = nextRate
  }

  function formatTime(time) {
    return formatTimeCompact(time)
  }

  function cleanupPlayback() {
    stopCurrent()
    detachVideoElement()
    currentTime.value = 0
    audioDuration.value = 0
    progressPercentage.value = 0
    currentSubtitleIndex.value = 0
  }

  watch(displaySubtitles, (subtitles) => {
    if (!subtitles?.length) {
      currentSubtitleIndex.value = 0
      return
    }
    const subtitleIndex = findSubtitleIndexAtTime(currentTime.value)
    currentSubtitleIndex.value = subtitleIndex === -1 ? 0 : subtitleIndex
  }, { immediate: true })

  return {
    sound,
    mediaElement,
    isPlaying,
    currentTime,
    audioDuration,
    volume,
    playbackRate,
    progressPercentage,
    currentSubtitleIndex,
    activeSoundId,
    audioError,
    tooltipPosition,
    tooltipTime,
    showTooltip,
    attachVideoElement,
    detachVideoElement,
    startTimeUpdate,
    stopTimeUpdate,
    checkCurrentSubtitle,
    getCurrentSubtitle,
    handleProgressClick,
    handleProgressHover,
    hideTimeTooltip,
    setVolume,
    setPlaybackRate,
    handleAudioError: handleVideoError,
    formatTime,
    playCurrent,
    pauseCurrent,
    stopCurrent,
    seekCurrent,
    isCurrentPlaying,
    cleanupPlayback,
    setOnPauseCallback,
    setOnEndedCallback,
    setOnPlayCallback,
  }
}
